import { ClientSideValidationError } from '@atlassian/jira-business-error-handling/src/utils/client-side-validation-error/index.tsx';
import type { IntlShape } from '@atlassian/jira-intl';
import type { ClipboardItem, ListIssueFieldTransformed, PasteMetadata } from '../types';
import { getRichOrStringValue, getStringValue } from '../value';
import { VALIDATION_ERROR_CODES } from './constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyPromise = Promise<any>;

export function assertClipboardHasPlainTextFormat(
	clipboardItem: ClipboardItem | undefined,
): clipboardItem is { plainText: string } {
	if (!clipboardItem?.plainText) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.EMPTY_CLIPBOARD);
	}

	return true;
}

export function assertClipboardHasRichFormat(
	clipboardItem: ClipboardItem | undefined,
): clipboardItem is { plainText: string; html: string } {
	assertClipboardHasPlainTextFormat(clipboardItem);
	if (!clipboardItem?.html) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_COMPATIBLE_FIELD_TYPE);
	}

	return true;
}

export function assertClipboardDataIsCompatible(
	metadata: PasteMetadata | null,
	compatibleTypes: string[],
): metadata is PasteMetadata {
	if (!metadata?.fieldType || !compatibleTypes.includes(metadata?.fieldType)) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_COMPATIBLE_FIELD_TYPE);
	}

	return true;
}

function getStringifiedRichFieldValue<T>(richValue: T | null) {
	if (!richValue) {
		return null;
	}

	if (typeof richValue === 'string') {
		return { value: richValue };
	}

	return richValue;
}

const safelyAddRichValueToHtml = (
	html: string,
	richValue: ReturnType<typeof getStringifiedRichFieldValue>,
) => {
	try {
		const DOM = new DOMParser().parseFromString(html, 'text/html');
		DOM.body?.querySelector('div')?.setAttribute('data-field-value', JSON.stringify(richValue));
		return DOM.body?.innerHTML || '';
	} catch (e) {
		return html;
	}
};

/**
 * getFieldValueAsClipboardItem retrieves the value of the field provided by the hook
 * and turn it into a ClipboardItem object.
 */
export function getFieldValueAsClipboardItem<T>(
	field: ListIssueFieldTransformed,
	issue: {
		key?: string;
		issueId?: string;
		issueType?: {
			level?: number;
		};
	},
	textValue: string | null,
	value: T | null,
): ClipboardItem {
	const richValue = getStringifiedRichFieldValue(value);

	if (!textValue) {
		return {};
	}

	const html = `<div data-type="list-cell" data-issue-type-level="${issue?.issueType?.level}" data-field-id="${field.fieldId}" data-field-type="${field.type}">${textValue}</div>`;

	return {
		html: richValue ? safelyAddRichValueToHtml(html, richValue) : html,
		plainText: textValue,
	};
}

export const addToClipboard = (plainText: string, html: string) => {
	const blobPlain = new window.Blob([plainText], { type: 'text/plain' });
	const blobHTML = new window.Blob([html], { type: 'text/html' });
	const clipboardItem = new window.ClipboardItem({
		'text/html': blobHTML,
		'text/plain': blobPlain,
	});

	return navigator.clipboard.write([clipboardItem]);
};

/**
 * getCellValue operates on apollo cache data, it's used as fallback for fields that
 * don't expose their values through a hook. Any field that allows cut or paste should
 * expose their values from the hook instead of relying on this method.
 */
export const getCellValue = (
	field: ListIssueFieldTransformed,
	issue?: {
		key?: string;
		issueId?: string;
		issueType?: {
			level?: number;
		};
	},
	formatMessage?: IntlShape['formatMessage'],
): ClipboardItem => {
	const textValue = getStringValue(field.type, field, formatMessage);
	const richValue = getStringifiedRichFieldValue(getRichOrStringValue(field.type, field));

	if (!textValue) {
		return {};
	}

	const html = `<div data-type="list-cell" data-issue-type-level="${issue?.issueType?.level}" data-field-id="${field.fieldId}" data-field-type="${field.type}">${textValue}</div>`;

	return {
		html: richValue ? safelyAddRichValueToHtml(html, richValue) : html,
		plainText: textValue,
	};
};
